define((require) => {
    "use strict";

    var _                     = require("underscore"),
        Backbone              = require("backbone"),
        EditorEvents          = require("shared/services/EditorEvents"),
        ImprovedCompositeView = require("zbone/ImprovedCompositeView"),
        Storage               = require("shared/services/Storage"),
        template              = require("zbone/FilteringCollectionView.tpl");

    /**
     * @class FilteringOptions
     */
    var FilteringOptions = Backbone.Model.extend({
        defaults: {
            // The key to retrieve the saved sorting options in the LocalStorage
            collectionName: null,
            // A list of attributes available for sorting
            sortAttributeList: null,
            // An attribute to sort against the collection
            sortAttribute: "editedAt",
            // The sorting ascending
            sortAscending: false,
            /**
             * The list of attributes used to filter against a model when non-specific {@see #filterAttribute} is set.
             *
             * The filterAttributeList defines a scope for filter operation. For example, if list contains
             * ["name", "date"], then when we do filter, the provided key must match against the value of name/date
             * in the model. If no such scope is specified, then the filter will search through the whole model.
             */
            filterAttributeList: [],
            // An attribute used to filter against the collection
            filterAttribute: "",
            // A key to filter against the collection
            searchKey: ""
        },

        initialize: function() {
            var savedSortAttribute = Storage.Local.get(this.get("collectionName"), "sortingOption", this.get("sortAttribute"));
            this.set("sortAttribute", savedSortAttribute);
            this.set("sortAscending", this._getAscendingBySort(savedSortAttribute));

            this.on("change:sortAttribute", this._storeSortAttribute);
        },

        _storeSortAttribute: function() {
            Storage.Local.update(this.get("collectionName"), "sortingOption", this.get("sortAttribute"));
        },

        _getAscendingBySort: function(sortAttribute) {
            var ascending = this.get("sortAscending"),
                list = this.get("sortAttributeList"),
                match;

            if (list) {
                match = _.findWhere(list, {value: sortAttribute});
                if (match) {
                    ascending = match.ascending;
                }
            }
            return ascending;
        }
    });

    /**
     * A CollectionView that can be sorted, filtered and searched using built-in controls.
     *
     * Note that due to limitations on how sorting works, the collection used must be fetched from the server before creating this object.
     * Note, the `events` object needs to be extended (and not overwritten) by subclasses.
     *
     *
     * @author Bo Gotthardt
     * @class FilteringCollectionView
     * @constructor
     *
     * @cfg {Boolean}   [sortAscending = true] Direction to sort in.
     * @cfg {String}    [sortAttribute = "createdAt"] Model attribute to sort on.
     * @cfg {String}    [searchKey = ""] Value to filter models by.
     * @cfg {Array}     [filterAttributeList = []] Model attributes to filter on.
     * @cfg {Object[]}  [sortAttributeList = [{label: "Name", value: "name"}]] List of values and labels to show in the sort attribute dropdown.
     */
    return ImprovedCompositeView.extend({

        template: template,

        templateHelpers: function() {
            return {
                sortAttributeList: this.model.get("sortAttributeList")
            };
        },

        className: "filtering-collection-view",

        itemViewContainer: ".collection",

        model: null,

        events: {
            "click .sorting-tabs span": "_sortingTabsHandler"
        },

        ui: {
            search: ".search",
            filterDropdown: ".filter-attribute"
        },

        modelEvents: {
            "change:sortAscending": "_sortAndFilter",
            "change:sortAttribute": "_sortAndFilter",
            "change:filterAttribute": "filter",
            "change:searchKey": "throttleFilter"
        },

        bindings: {
            "input.search": "searchKey",
            "select.filter-attribute": {
                observe: "filterAttribute",
                selectOptions: {
                    collection: function() {
                        return this.filterAttributeList;
                    }
                }
            }
        },

        constructor: function(options) {
            // Assigning here to avoid prototype sharing problem, and get it included in the superclass constructor logic.
            this.model = new FilteringOptions({
                filterAttributeList: this.filterAttributeList,
                sortAttributeList: this.sortAttributeList,
                collectionName: this.collectionName,
                sortAttribute: this.defaultSortAttribute
            });

            // We need to modify this.collection in order to implement sorting and filtering.
            // But if the collection is used for other things, they will unexpectedly get those modifications as well.
            // So we need to replace this.collection with a clone, and keep a reference to the original so we can duplicate add and remove in the clone.
            if (!options.collection) return;
            this.originalCollection = options.collection;
            // The collection will be set in the resetCollection, because we need to do sort and filter first.
            options.collection = new Backbone.Collection();
            this.originalCollection.comparator = this.sortComparator.bind(this);
            this.originalCollection.sort();

            // Superclass constructor here so we can shuffle around options.collection first.
            this.__super();

            this.listenTo(this.originalCollection, "add", function(oneOrMoreModels) {
                this._updateEmptyView();

                if (!_.isArray(oneOrMoreModels)) {
                    oneOrMoreModels = [oneOrMoreModels];
                }

                this.collection.add(_.filter(oneOrMoreModels, this.filterComparator()));
                this.filter();
            });

            this.listenTo(this.originalCollection, "remove", function(oneOrMoreModels) {
                this._updateEmptyView();
                this.collection.remove(oneOrMoreModels);
            });

            // Back up the empty view and do the initial update
            this._originalEmptyView = this.emptyView;
            this._updateEmptyView();
        },

        onRender: function() {
            if (!this.options.filterDropdown) {
                this.ui.filterDropdown.remove();
                this.ui.search.addClass("full-width");
            }

            this._filterAndUpdate();
        },

        _sortingTabsHandler: function(event) {
            var sortBy = event.target.getAttribute("data-sort-by"),
                attribute = _.findWhere(this.sortAttributeList, {value: sortBy});

            event.preventDefault();
            this.updateSort(attribute.value, attribute.ascending);

            var eventProperties = {
                sortBy: attribute.label,
                triggerOrigin: "SortingPanel"
            };

            EditorEvents.trigger(EditorEvents.constants.SORT_DASHBOARD, eventProperties);
        },

        /**
         * Sorts the collection according to the sortAttribute and ascending.
         *
         * If invoked with no arguments, sorting/rendering is updated immediately.
         *
         * @param {String} value - The sort attribute by which we want to sort.
         * @param {Boolean} ascending - true = ascending, false = descending.
         */
        updateSort: function(value, ascending) {
            if (arguments.length === 0) {
                return this._sortAndFilter();
            }

            // Let the first setting be silent, otherwise will trigger sorting twice
            this.model.set("sortAscending", ascending, {silent: true});
            this.model.set("sortAttribute", value);
        },

        /**
         * Updates the tabs to indicate the current sorting attribute.
         *
         * @param {String} sortAttribute - "createdAt", "name", etc
         */
        _updateActiveTab: function(sortAttribute) {
            if (_.contains(_.pluck(this.sortAttributeList, "value"), sortAttribute)) {
                this.$(".sorting-tabs .active").removeClass("active");
                this.$("[data-sort-by=" + sortAttribute + "]").addClass("active");
            }
        },

        /**
         * When the original collection is empty, we use the empty view specified by the user. However, if the original
         * collection is not empty, we want to use the "no results" view as the empty view.
         *
         * @private
         */
        _updateEmptyView: function() {
            if (this.originalCollection.length === 0) {
                this.$el.addClass("empty");
                this.emptyView = this._originalEmptyView;
            } else {
                this.$el.removeClass("empty");
                this.emptyView = this.noResultsItemView || this._originalEmptyView;
            }
        },

        /**
         * Returns a function to check whether the model should be filtered out.
         *
         * @param  {String} [filterAttribute] - If empty or undefined, it will use the filter string
         * from the FilteringOptions (model.get("filter")).
         *
         * @private
         */
        filterComparator: function(filterAttribute, value) {
            var filterList = this.model.get("filterAttributeList"),
                self = this;

            // TODO: Get changes from custom JS task (commit 67fdc7b)
            return function(model) {
                var scope;

                if (!filterAttribute && !value) {
                    return true;
                } else if (!filterAttribute) {
                    scope = filterList;
                } else {
                    scope = filterAttribute;
                }

                return self.containsValue(model, scope, value);
            };
        },

        /**
         * Given a model, attributes and a key, find if the model contains the key in any values of supplied attributes.
         *
         * @param {Backbone.Model} model
         * @param {Array} attrs
         * @param {String} key
         * @returns {boolean}
         */
        containsValue: function(model, attrs, key) {
            key = key || "";
            var haystack = "";
            attrs = _.isArray(attrs) ? attrs : [attrs];

            _.each(attrs, (attr) => {
                haystack += model.get(attr);
            });

            return haystack.toLocaleLowerCase().indexOf(key.toLocaleLowerCase()) !== -1;
        },

        /**
         * A rate limited filter when users are typing the search key
         */
        throttleFilter: _.throttle(function() {
            this.filter();
        }, 300),

        /**
         * Filter the collection and reset the collection with the result, which will trigger a re-render
         *
         * @private
         */
        filter: function() {
            var filterAttribute = this.model.get("filterAttribute"),
                key = this.model.get("searchKey"),
                searchKey = _.isString(key) ? key.trim() : "",
                // User-specified attribute to filter on...
                userSpecifiedAttribute = searchKey.split(/\s*::\s*/, 2); // only split on first double colon to avoid conflict with names with colon in them

            if (userSpecifiedAttribute.length === 2) {
                searchKey = userSpecifiedAttribute.pop();
                filterAttribute = userSpecifiedAttribute;
            }

            var filterFunction  = this.filterComparator(filterAttribute, searchKey),
                items = this.originalCollection.filter(filterFunction);

            this._resetCollection(items);
        },

        /**
         * Filter the collection by the given key
         *
         * @param {String} key
         */
        filterBy: function(key) {
            this.model.set("searchKey", key);
        },

        /**
         * An usual sort comparator for the original collection when sorting
         *
         * @param {Backbone.Model} a
         * @param {Backbone.Model} b
         * @returns {number}
         */
        sortComparator: function(a, b) {
            var sortAttr = this.model.get("sortAttribute"),
                aVal = a.get(sortAttr),
                bVal = b.get(sortAttr),
                comparison;

            if (_.isString(aVal)) {
                comparison = aVal.localeCompare(bVal);
            } else {
                comparison = aVal - bVal;
            }

            return (this.model.get("sortAscending") ? 1 : -1) * comparison;
        },

        /**
         * Sort the collection and sequentially do a filter if there is a search key
         * @private
         */
        _sortAndFilter: function() {
            // We only need to sort in the beginning and when sort attribute changes, no need to sort every render
            this.originalCollection.sort();
            this._filterAndUpdate();
        },

        /**
         * Filter the collection and update the tabs
         * @private
         */
        _filterAndUpdate: function() {
            this.filter();
            this._updateActiveTab(this.model.get("sortAttribute"));
        },

        /**
         * Reset the shown collection.
         *
         * @param  {Array} items - Array of models from the collection (E.g. Images, Experiences)
         * @private
         */
        _resetCollection: function(items) {
            this.collection.reset(items, {merge: false});
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/zbone/FilteringCollectionView.js